import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { LazyMotion, domAnimation, m, AnimatePresence } from "framer-motion";
import { cn, scrollToElement } from "@/lib/utils";
import { slideIn } from "@/lib/animations";
import { SafeImage } from "@/components/ui/safe-image";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Close mobile menu when route changes
    setIsMenuOpen(false);
  }, [location]);

  const handleNavLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string, id: string) => {
    setIsMenuOpen(false);

    // If we're on the home page and clicking a home link, scroll to the element
    if (path === "/" && location === "/") {
      e.preventDefault();
      scrollToElement(id);
    }
    // If we're clicking Home from another page, navigate normally
    // All other pages will handle their own navigation
  };

  // "Home" compare solo nel menu mobile: su desktop il logo porta già alla home
  // e lo spazio serve alle altre voci
  const navigationLinks = [
    { name: "Home", id: "home", path: "/", mobileOnly: true },
    { name: "Metodo", id: "metodo", path: "/metodo" },
    { name: "Piatti", id: "piatti", path: "/ricette?category=Dolci" },
    { name: "Testimonianze", id: "testimonianze", path: "/testimonianze" },
    { name: "Formazione", id: "formazione", path: "/formazione" },
    { name: "Shop", id: "shop", path: "/shop" },
    { name: "Press", id: "press", path: "/press" },
    { name: "Riflessioni", id: "riflessioni", path: "/riflessioni" },
    { name: "Contatti", id: "contatti", path: "/contatti" }
  ];

  const isActiveLink = (path: string) => {
    // startsWith: la voce resta attiva anche dentro un articolo (/riflessioni/:slug)
    return location === path || location.startsWith(`${path}/`);
  };

  // Animated indicator for hover effect
  const hoverIndicator = {
    hidden: { width: 0, opacity: 0 },
    visible: {
      width: "100%",
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  return (
    <LazyMotion features={domAnimation}>
      <header className={cn(
        "fixed w-full z-50 transition-all duration-500",
        isScrolled
          ? "dark-glass backdrop-blur-md py-2 shadow-[0_2px_20px_rgba(0,0,0,0.1)]"
          : "bg-transparent py-4"
      )}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link href="/" className="flex items-center flex-shrink-0">
            <SafeImage
              src="logo-metodo-bianchini-white"
              alt="MetodoBianchini Logo"
              useFallbackService={true}
              className={cn(
                "transition-all duration-500",
                isScrolled ? "h-10" : "h-14"
              )}
            />
          </Link>

          {/* Desktop Navigation. Solo da xl in su: con 8 voci e 2 pulsanti il menu esteso
              non entra sotto i 1280px (su tablet e portatili piccoli i pulsanti finivano tagliati) */}
          <nav className="hidden xl:flex space-x-4 items-center ml-6">
            {navigationLinks.filter(link => !link.mobileOnly).map(link => (
              <div key={link.id} className="relative group">
                <a
                  href={link.path}
                  onClick={(e) => handleNavLinkClick(e, link.path, link.id)}
                  className={cn(
                    "font-montserrat font-medium text-sm transition-colors py-2 px-1",
                    isActiveLink(link.path)
                      ? "text-primary"
                      : "text-white hover:text-white/90"
                  )}
                  aria-label={link.name}
                  aria-current={isActiveLink(link.path) ? "page" : undefined}
                >
                  {link.name}
                </a>
                <m.div
                  className={cn(
                    "absolute bottom-0 left-0 h-0.5 origin-left bg-primary",
                    isActiveLink(link.path) && "w-full opacity-100"
                  )}
                  initial="hidden"
                  whileHover="visible"
                  variants={hoverIndicator}
                />
              </div>
            ))}
            <a
              href="/#consulenze"
              onClick={(e) => {
                e.preventDefault();
                if (location === "/") {
                  scrollToElement("consulenze");
                } else {
                  setLocation("/");
                  // Aspetta che l'elemento esista nel DOM
                  const waitForElement = () => {
                    const element = document.getElementById("consulenze");
                    if (element) {
                      scrollToElement("consulenze");
                    } else {
                      requestAnimationFrame(waitForElement);
                    }
                  };
                  requestAnimationFrame(waitForElement);
                }
              }}
              className="ml-2 border border-primary text-primary font-montserrat font-medium text-sm rounded-lg hover:bg-primary hover:text-white transition-all px-4 py-2 whitespace-nowrap"
            >
              Consulenza
            </a>
            <Link
              href="/supporto"
              className="bg-primary text-white font-montserrat font-medium text-sm rounded-lg hover:bg-primary/90 transition-all px-4 py-2 whitespace-nowrap"
            >
              Assistenza al percorso
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden text-white p-2 dark-glass rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <m.nav
              id="mobile-navigation"
              className="xl:hidden dark-glass shadow-lg absolute w-full backdrop-blur-lg border-t border-white/10"
              variants={slideIn}
              initial="hidden"
              animate="visible"
              exit="exit"
              role="navigation"
              aria-label="Mobile navigation"
            >
              <div className="container mx-auto px-4 py-6 flex flex-col space-y-5">
                {navigationLinks.map(link => (
                  <a
                    key={link.id}
                    href={link.path}
                    onClick={(e) => handleNavLinkClick(e, link.path, link.id)}
                    className={cn(
                      "font-montserrat font-medium py-2 transition-colors border-b border-white/10 pb-3",
                      isActiveLink(link.path)
                        ? "text-primary border-primary/30"
                        : "text-white hover:text-primary"
                    )}
                    aria-current={isActiveLink(link.path) ? "page" : undefined}
                  >
                    {link.name}
                  </a>
                ))}
                <a
                  href="/#consulenze"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMenuOpen(false);
                    if (location === "/") {
                      scrollToElement("consulenze");
                    } else {
                      setLocation("/");
                      // Aspetta che l'elemento esista nel DOM
                      const waitForElement = () => {
                        const element = document.getElementById("consulenze");
                        if (element) {
                          scrollToElement("consulenze");
                        } else {
                          requestAnimationFrame(waitForElement);
                        }
                      };
                      requestAnimationFrame(waitForElement);
                    }
                  }}
                  className="mt-4 border border-primary text-primary font-montserrat font-medium rounded-lg hover:bg-primary hover:text-white transition-all text-center block px-4 py-2"
                >
                  Consulenza
                </a>
                <Link
                  href="/supporto"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-primary text-white font-montserrat font-medium rounded-lg hover:bg-primary/90 transition-all text-center block px-4 py-2"
                >
                  Assistenza al percorso
                </Link>
              </div>
            </m.nav>
          )}
        </AnimatePresence>
      </header>
    </LazyMotion>
  );
}
